import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DayOfWeek, UserNutritionalNeeds, WeekPlan } from '@/api/types'
import { Button } from '@/components/Button'
import { Grid } from '@/components/Grid'
import { Tab, TabList, TabPanel, Tabs } from '@/components/Tabs'
import { groupBy } from '@/utils'
import { DAYS_OF_WEEK, DAYS_OF_WEEK_JS } from '../constants'
import DailyEnergyRequirements from '../DailyEnergyRequirements/DailyEnergyRequirements'
import DayMealPlan from './DayMealPlan'
import './MealPlan.css'
import { GetReplacementsFn } from './CreateMealPlan'

type Props = {
  weekPlan: WeekPlan
  userNutritionalNeeds?: UserNutritionalNeeds
  mainUserId: number
  onReplaceMeal: any
  simulateMealPlan: () => void
  getReplacements: GetReplacementsFn
  replacedMealIds: Record<string, boolean>
}

export default function MealPlan({
  weekPlan,
  userNutritionalNeeds,
  mainUserId,
  onReplaceMeal,
  simulateMealPlan,
  getReplacements,
  replacedMealIds,
}: Props) {
  const { t } = useTranslation()
  const groupedPlans = groupBy(weekPlan.mealPlans, 'dayOfWeek')
  const [selectedDay, setSelectedDay] = React.useState<DayOfWeek>(
    DAYS_OF_WEEK_JS[new Date().getDay()],
  )
  const dayMealPlans = useMemo(
    () => weekPlan.mealPlans.filter((mp) => mp.dayOfWeek === selectedDay),
    [selectedDay, weekPlan],
  )

  return (
    <Tabs selectedKey={selectedDay} onSelectionChange={(key) => setSelectedDay(key as DayOfWeek)}>
      <div className="MealPlan-tabsHeader panel-xs">
        <TabList>
          {DAYS_OF_WEEK.map((day) => (
            <Tab key={day} id={day}>
              {t(`profile.dayOfWeek.${day}`)}
            </Tab>
          ))}
        </TabList>
        <Button onPress={simulateMealPlan} style={{ alignSelf: 'end' }}>
          {t('mealSimulation.regenerate')}
        </Button>
      </div>

      <Grid columns="3fr 1fr">
        {DAYS_OF_WEEK.map((day) => (
          <TabPanel key={day} id={day}>
            <DayMealPlan
              key={day}
              dayPlan={groupedPlans[day] || []}
              mainUserId={mainUserId}
              dayOfWeek={day}
              onReplaceMeal={onReplaceMeal}
              getReplacements={getReplacements}
              replacedMealIds={replacedMealIds}
            />
          </TabPanel>
        ))}

        <DailyEnergyRequirements
          mealPlans={dayMealPlans}
          nutritionalNeeds={userNutritionalNeeds}
          mainUserId={mainUserId}
        />
      </Grid>
    </Tabs>
  )
}
